import { useTranslation } from 'react-i18next'
import { VoicePairReadNotice } from '@/components/humint/voice/match/VoicePairReadNotice'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { classifySubjectReadError } from '@/features/humint/mappers/subject-page'
import { useVoiceAb } from '@/features/humint/voice/useVoiceAb'
import { useVoicePairClips } from '@/features/humint/voice/useVoicePairClips'
import type { RecordingVoiceMatch } from '@/services/api/recording-voice-matches'
import { type RecordingMatchActions, RecordingMatchView } from './RecordingMatchView'
import { recordingMatchClips } from './recording-match-model'
import { type RecordingMatchContext, useRecordingMatch } from './useRecordingMatch'

function MatchListening({
  match,
  actions,
}: {
  match: RecordingVoiceMatch
  actions: RecordingMatchActions
}) {
  const clips = useVoicePairClips(recordingMatchClips(match))
  const transport = useVoiceAb(clips.sources, clips.request)
  return <RecordingMatchView match={match} actions={actions} transport={transport} />
}

function MatchRequest({ context }: { context: RecordingMatchContext }) {
  const { t } = useTranslation()
  const { query, actions } = useRecordingMatch(context)
  if (query.isPending) return <Skeleton className="h-96 w-full" />
  if (query.isError || !query.data) {
    const failure = classifySubjectReadError(query.error, navigator.onLine)
    if (failure.kind === 'not_member')
      return <p role="alert">{t('humint.tattooMatch.change.problem.forbidden')}</p>
    return (
      <VoicePairReadNotice
        problem={failure.problem}
        retrying={query.isFetching}
        onRetry={() => void query.refetch()}
      />
    )
  }
  return <MatchListening match={query.data} actions={actions} />
}

export function RecordingMatchDialog({
  context,
  onClose,
}: {
  context: RecordingMatchContext | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={Boolean(context)}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t('humint.voice.match.title')}</DialogTitle>
          <DialogDescription>{t('identity.detail.audio.match.description')}</DialogDescription>
        </DialogHeader>
        {context && <MatchRequest key={context.matchId} context={context} />}
      </DialogContent>
    </Dialog>
  )
}
